import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
// type-coverage:ignore-next-line
import { Injectable, PLATFORM_ID, inject, signal, computed } from '@angular/core';
import { form, required, email } from '@angular/forms/signals';
import { FormItem } from '@shared/form-item/form.item.type';
import { FormSendingDialogService } from '@shared/form-sending-dialog/form-sending-dialog.service';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { EMPTY, catchError, tap } from 'rxjs';

@Injectable()
export class CareerDetailsTellUsService {
    private readonly http = inject(HttpClient);

    private readonly _file = signal<File | null>(null);
    private readonly _fileError = signal<string>('');

    private readonly platformId = inject<string>(PLATFORM_ID);
    private readonly formSendingDialogService = inject(FormSendingDialogService);

    public fileAndError = computed(() => ({
        file: this._file(),
        fileError: this._fileError(),
    }));

    public form = form(
        signal({
            name: '' as string,
            lastName: '' as string,
            email: '' as string,
        }),
        (p) => {
            required(p.name);
            required(p.lastName);
            required(p.email);
            email(p.email);
        }
    );

    public readonly steps: FormItem[] = [
        {
            formControlName: 'name',
            placeholder: 'First name',
        },
        {
            formControlName: 'lastName',
            placeholder: 'Last Name',
        },
        {
            formControlName: 'email',
            placeholder: 'Email',
        },
    ];

    public dropped(files: NgxFileDropEntry[]): void {
        const firstFile = files?.[0];
        if (firstFile) {
            const extensions = ['pdf', 'docx', 'doc'];
            //type-coverage:ignore-next-line
            const fileEntry = firstFile.fileEntry as FileSystemFileEntry;

            fileEntry.file((file: File) => {
                const extension: string = file.name.split('.').at(-1) ?? '';
                const fileSizeInMb = file.size / (1024 * 1024);
                if (!extensions.includes(extension)) {
                    this.setFileError('Only pdf, docx, doc');
                    return;
                }
                if (fileSizeInMb > 10) {
                    this.setFileError('File is large');
                    return;
                }
                this._file.set(file);
                this._fileError.set('');
            });
        }
    }

    public clearFile(): void {
        this._file.set(null);
        this._fileError.set('');
    }

    private setFileError(error: string): void {
        if (error) {
            this._file.set(null);
            this._fileError.set(error);
        }
    }

    public sendEmail(title: string): void {
        const file = this._file();
        if (this.form().invalid() || !file) {
            this.form().markAsTouched();
            return;
        }
        const { name, email, lastName } = this.form().value();
        this.formSendingDialogService.open();
        if (isPlatformServer(this.platformId)) {
            return;
        }
        const formData = new FormData();
        const cv = this._file();
        if (email && name && lastName && cv) {
            formData.append('email', email);
            formData.append('name', name);
            formData.append('lastName', lastName);
            formData.append('file', cv, cv.name);
            formData.append('title', title);
            this.http
                .post<string>('/send-cv', formData)
                .pipe(
                    tap(() => {
                        this.resetForm();
                        return this.formSendingDialogService.changeToSuccess();
                    }),
                    catchError(() => {
                        this.resetForm();
                        this.formSendingDialogService.changeToError();
                        return EMPTY;
                    })
                )
                .subscribe();
        }
    }

    public resetForm(): void {
        this.form().reset();
        this._file.set(null);
    }
}
